import { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { backgroundAPI, categoryAPI } from "../../lib/api";
import { useLanguage } from "../../context/LanguageContext";
import UserLayout from "../../components/UserLayout";
import ImageUploader from "../../components/ImageUploader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const AdminBackgrounds = () => {
    const { t } = useLanguage();
    const [backgrounds, setBackgrounds] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        prompt: "",
        thumbnail_url: "",
        category_id: "",
        tokens: 5,
        is_active: true
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [bgRes, catRes] = await Promise.all([
                backgroundAPI.getAll(),
                categoryAPI.getAll()
            ]);
            setBackgrounds(bgRes.data);
            setCategories(catRes.data);
        } catch (error) {
            toast.error(t('loading') + " error");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.thumbnail_url) {
            toast.error(t('upload_thumbnail'));
            return;
        }
        
        if (!formData.category_id) {
            toast.error(t('select_category_first'));
            return;
        }
        
        setSaving(true);

        try {
            if (editing) {
                await backgroundAPI.update(editing, formData);
            } else {
                await backgroundAPI.create(formData);
            }
            toast.success(t('background_saved'));
            setDialogOpen(false);
            fetchData();
            resetForm();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Error");
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (bg) => {
        setEditing(bg.id);
        setFormData({
            name: bg.name,
            prompt: bg.prompt,
            thumbnail_url: bg.thumbnail_url,
            category_id: bg.category_id,
            tokens: bg.tokens || 5,
            is_active: bg.is_active
        });
        setDialogOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('delete_confirm'))) return;
        
        try {
            await backgroundAPI.delete(id);
            toast.success(t('background_deleted'));
            fetchData();
        } catch (error) {
            toast.error("Error");
        }
    };

    const resetForm = () => {
        setEditing(null);
        setFormData({ name: "", prompt: "", thumbnail_url: "", category_id: "", tokens: 5, is_active: true });
    };

    const getCategoryName = (id) => categories.find(c => c.id === id)?.name || "-";

    return (
        <UserLayout>
            <div className="space-y-6" data-testid="admin-backgrounds">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="font-heading text-2xl font-bold text-gray-900">{t('backgrounds_title')}</h1>
                        <p className="text-gray-600">{t('backgrounds_subtitle')}</p>
                    </div>
                    <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="rounded-xl" data-testid="add-background-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        {t('add_background')}
                    </Button>
                </div>

                <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t('preview')}</TableHead>
                                <TableHead>{t('name')}</TableHead>
                                <TableHead>{t('category')}</TableHead>
                                <TableHead>{t('tokens')}</TableHead>
                                <TableHead>{t('prompt')}</TableHead>
                                <TableHead>{t('status')}</TableHead>
                                <TableHead className="text-right">{t('actions')}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center py-8">
                                        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                                    </TableCell>
                                </TableRow>
                            ) : backgrounds.length > 0 ? (
                                backgrounds.map((bg) => (
                                    <TableRow key={bg.id}>
                                        <TableCell>
                                            <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100">
                                                <img src={bg.thumbnail_url} alt={bg.name} className="w-full h-full object-cover" />
                                            </div>
                                        </TableCell>
                                        <TableCell className="font-medium">{bg.name}</TableCell>
                                        <TableCell>{getCategoryName(bg.category_id)}</TableCell>
                                        <TableCell>
                                            <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium">
                                                {bg.tokens || 5}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-gray-500 max-w-xs truncate">{bg.prompt}</TableCell>
                                        <TableCell>
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${bg.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                                {bg.is_active ? t('active') : t('inactive')}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-2">
                                                <Button size="icon" variant="ghost" onClick={() => handleEdit(bg)} data-testid={`edit-bg-${bg.id}`}>
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                <Button size="icon" variant="ghost" className="text-red-500" onClick={() => handleDelete(bg.id)} data-testid={`delete-bg-${bg.id}`}>
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                                        No data
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>

                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>{editing ? t('edit_background') : t('add_new_background')}</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label>{t('name')}</Label>
                                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="bg-name-input" />
                            </div>
                            <div className="space-y-2">
                                <Label>{t('category')}</Label>
                                <Select value={formData.category_id} onValueChange={(value) => setFormData({ ...formData, category_id: value })}>
                                    <SelectTrigger data-testid="bg-category-select">
                                        <SelectValue placeholder={t('select_category')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {categories.map(cat => (
                                            <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>{t('token_cost')}</Label>
                                <Input 
                                    type="number" 
                                    value={formData.tokens} 
                                    onChange={(e) => setFormData({ ...formData, tokens: parseInt(e.target.value) || 0 })} 
                                    min={1}
                                    required 
                                    data-testid="bg-tokens-input" 
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>{t('ai_prompt')}</Label>
                                <Textarea value={formData.prompt} onChange={(e) => setFormData({ ...formData, prompt: e.target.value })} required placeholder="Describe the background for AI..." data-testid="bg-prompt-input" />
                            </div>
                            <div className="space-y-2">
                                <Label>{t('thumbnail')}</Label>
                                <ImageUploader
                                    value={formData.thumbnail_url}
                                    onChange={(url) => setFormData({ ...formData, thumbnail_url: url })}
                                    label="background-thumbnail"
                                />
                            </div>
                            <div className="flex items-center justify-between">
                                <Label>{t('status')} {t('active')}</Label>
                                <Switch checked={formData.is_active} onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })} />
                            </div>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t('cancel')}</Button>
                                <Button type="submit" disabled={saving} data-testid="save-bg-btn">
                                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                    {saving ? t('loading') : t('save')}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
        </UserLayout>
    );
};

export default AdminBackgrounds;
